// LogoRequiredModal.
//
// Per PRINT_TEMPLATE_PLAN.md §6.6 + Phase 9: shown by the builder
// when the active template wants to render a header logo
// (`header.logo.show === true`) but the company has not uploaded one
// yet (`companies.logo_url === null`). Two CTAs:
//   1. "Go to Company Settings" — primary; navigates to /settings/company.
//   2. "Disable Logo for This Template" — secondary; clears the
//      `header.logo.show` flag in the builder's local state.
//
// Closing the modal without acting is intentionally not supported —
// staff are forced to make an explicit choice (per plan §6.6 "no
// way to dismiss without acting"). React Query's
// refetchOnWindowFocus on `useCompanyOverview` means that if the
// user uploads a logo in another tab and returns, the modal
// auto-dismisses on the next focus.

import { Link } from 'react-router-dom';
import { Building2, Image } from 'lucide-react';

import { Modal } from '../../../../components/ui/Modal/Modal';
import { Button } from '../../../../components/ui/Button/Button';
import styles from './LogoRequiredModal.module.css';

export interface LogoRequiredModalProps {
  open: boolean;
  /** Sets `header.logo.show = false` in the parent's local state and
   *  closes the modal. Also marks the form as dirty (handled
   *  implicitly via the parent's content-vs-server diff). */
  onDisableLogo: () => void;
  /** Same handler as `onDisableLogo` per plan §6.6 — there is no
   *  "dismiss without acting" path. The Modal's Esc / backdrop
   *  paths route through this. */
  onClose: () => void;
}

export function LogoRequiredModal({
  open,
  onDisableLogo,
  onClose,
}: LogoRequiredModalProps): JSX.Element {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Company Logo Required"
      size="sm"
      preventClose
      footer={
        <>
          <Button
            variant="secondary"
            icon={Image}
            onClick={onDisableLogo}
          >
            Disable Logo for This Template
          </Button>
          <Link to="/settings/company" className={styles.link}>
            <Button variant="primary" icon={Building2}>
              Go to Company Settings
            </Button>
          </Link>
        </>
      }
    >
      <p className={styles.body}>
        Your template is set to show your company logo, but no logo has been
        uploaded yet. Upload a logo in Company Settings or disable the logo
        for this template.
      </p>
    </Modal>
  );
}
